import { useState, useEffect } from "react";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Building2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

export default function CustomerListPage() {
  const [customers, setCustomers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", company_id: "", contact_person: "", phone: "", email: "", address: "" });

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data.items || [])).catch(() => {});
    api.get("/admin/companies").then((r) => setCompanies(r.data || [])).catch(() => {});
  }, []);

  const handleCreate = async () => {
    try {
      await api.post("/customers", form);
      toast.success("Customer created");
      setShowCreate(false);
      setForm({ code: "", name: "", company_id: "", contact_person: "", phone: "", email: "", address: "" });
      const r = await api.get("/customers");
      setCustomers(r.data.items || []);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const filtered = customers.filter((c) =>
    !searchTerm || c.name?.toLowerCase().includes(searchTerm.toLowerCase()) || c.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div data-testid="customer-list-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>Customers</h1>
        <Button data-testid="new-customer-button" onClick={() => setShowCreate(true)} className="bg-[#0052CC] hover:bg-[#003D99] rounded-sm">
          <Plus size={16} className="mr-1.5" /> New Customer
        </Button>
      </div>

      <Card className="border border-gray-200 shadow-sm rounded-sm">
        <CardContent className="p-4">
          <Input data-testid="customer-search" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search customers..." className="max-w-xs rounded-sm h-9 mb-4 border-gray-300" />
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs font-semibold uppercase tracking-[0.1em] text-gray-500 border-b">
                <th className="text-left py-3 px-3">Code</th>
                <th className="text-left py-3 px-3">Name</th>
                <th className="text-left py-3 px-3">Company</th>
                <th className="text-left py-3 px-3">Contact</th>
                <th className="text-left py-3 px-3">Phone</th>
                <th className="text-left py-3 px-3">Email</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-gray-400">No customers found</td></tr>}
              {filtered.map((c) => (
                <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-3 font-medium text-[#0052CC]">{c.code}</td>
                  <td className="py-3 px-3 text-gray-700">{c.name}</td>
                  <td className="py-3 px-3 text-xs text-gray-500">{c.company_name}</td>
                  <td className="py-3 px-3 text-gray-600">{c.contact_person || "—"}</td>
                  <td className="py-3 px-3 text-gray-500">{c.phone || "—"}</td>
                  <td className="py-3 px-3 text-gray-500">{c.email || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader><DialogTitle style={{ fontFamily: "Chivo, sans-serif" }}>New Customer</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-gray-500">Code</Label><Input data-testid="customer-code-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="rounded-sm h-9 mt-1" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-gray-500">Name</Label><Input data-testid="customer-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm h-9 mt-1" /></div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-500">Company</Label>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger className="rounded-sm h-9 mt-1"><SelectValue placeholder="Select company" /></SelectTrigger>
                <SelectContent>{companies.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider text-gray-500">Contact Person</Label><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="rounded-sm h-9 mt-1" /></div>
              <div><Label className="text-xs uppercase tracking-wider text-gray-500">Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-sm h-9 mt-1" /></div>
            </div>
            <div><Label className="text-xs uppercase tracking-wider text-gray-500">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm h-9 mt-1" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-gray-500">Address</Label><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-sm mt-1" rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="save-customer-button" onClick={handleCreate} className="bg-[#0052CC] hover:bg-[#003D99] rounded-sm">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
